'use client';

import { Locale } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { RefObject, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useDebounceCallback, useEventListener, useTimeout } from 'usehooks-ts';
import ViverseLogoOverlay from '@/app/[locale]/(worlds)/[roomId]/components/ViverseLogoOverlay';
import WorldSplash from '@/app/[locale]/(worlds)/[roomId]/components/WorldSplash';
import { BI_CATEGORY_BEHAVIOR_IN_ROOM, BI_EVENT_VIEW_COUNT } from '@/constants/bi-sdk';
import { IFRAME_SEARCH_PARAM_KEYS, IframeSearchParamKeys } from '@/constants/iframe';
import { useAuthContext } from '@/context/AuthContext';
import { useBISdkContext } from '@/context/BISdkContext';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { useUAContext } from '@/context/UAContext';
import { useVSCSdkContext } from '@/context/VSCSdkContext';
import { useWorldIframeToken } from '@/context/WorldIframeTokenContext';
import { useDisableScrollLock } from '@/hooks/useDisableScrollLock';
import { useEnableFull3d } from '@/hooks/useEnableFull3d';
import { useEnableViewMode } from '@/hooks/useEnableViewMode';
import { useFullscreenStatus } from '@/hooks/useFullscreenStatus';
import { IframeHandlers } from '@/hooks/useIframeScrollLock';
import { useIframeSrcTeardown } from '@/hooks/useIframeSrcTeardown';
import { useMessageDispatch } from '@/hooks/useMessageDispatch';
import { useMobileWorldRedirectUrlBuilder } from '@/hooks/useMobileWorldRedirectUrl';
import { useRouter } from '@/i18n/navigation';
import { getCreateDomain } from '@/lib/configs';
import { buildIframeUrl, getWorldIframeAllowPermissions } from '@/lib/iframe';
import { cn } from '@/lib/utils';
import { addWorldViewCount, LogoPosition } from '@/services/cms';
type WorldIframeProps = {
  ref: RefObject<HTMLIFrameElement | null>;
  handlers: IframeHandlers;
};
type WorldMessages = {
  'world-mounted': void;
  'sign-in': void;
  'url-change': {
    url: string;
  };
  'world-exit': void;
  'refresh-page': void;
  'locale-changed': {
    locale: Locale;
  };
  'debug-mode': {
    configs: unknown;
  };
  'done-update-vram': void;
  'fullscreen-request': {
    enter: boolean;
  };
};
function WorldIframe({
  handlers,
  ref
}: WorldIframeProps) {
  const {
    handleLogin
  } = useAuthContext();
  const {
    roomData,
    setRoomData
  } = useCheckEnterRoomContext();
  const {
    jwtToken
  } = useWorldIframeToken();
  const enableFull3d = useEnableFull3d();
  const enableViewMode = useEnableViewMode();
  const disableScrollLock = useDisableScrollLock();
  const getMobileRedirectUrl = useMobileWorldRedirectUrlBuilder();
  const searchParams = useSearchParams();
  const router = useRouter();
  const viverseProOwner = !!roomData?.owner.viverse_pro;
  const [isShowSplash, setIsShowSplash] = useState(!viverseProOwner);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isWebGLSupported, setIsWebGLSupported] = useState<boolean | null>(null);
  const {
    isMobile,
    isMobileLandscape
  } = useUAContext();
  const sceneSettings = roomData.scene.settings;
  const isViverseSystemDisabled = sceneSettings?.disabledViverseSystem;
  const [isLogoReadyToShow, setIsLogoReadyToShow] = useState(false);
  const isFull3D = searchParams.has(IFRAME_SEARCH_PARAM_KEYS.FULL3D);
  const {
    isFullscreen
  } = useFullscreenStatus();

  // Portrait = mobile device in portrait orientation (height >= width)
  // Matches PlayCanvas isWorldPortraitMode() / ViverseLogo !isPortrait check
  const isMobilePortrait = isMobile && !isMobileLandscape;
  const shouldRenderLogo = isLogoReadyToShow && !viverseProOwner && !isMobilePortrait && (
  // hidden on mobile portrait (both full3D and fullscreen)
  isFull3D || !isViverseSystemDisabled && isFullscreen);

  // PlayCanvas toolbar occupies bottom-right; lock logo there so it's consistently
  // offset by right-20 on desktop (avoiding the fullscreen button).
  const logoPosition: LogoPosition = 'bottom-right';
  const {
    sendBIReport
  } = useBISdkContext();
  const {
    accountType
  } = useConfigsContext();
  const handleAddWorldViewCount = async () => {
    if (roomData?.hub_sid) {
      const data = await addWorldViewCount(roomData.hub_sid);
      if (data?.status === 200) {
        const prevViewCount = roomData?.view_count || 0;
        setRoomData(prev => ({
          ...prev,
          view_count: prev.view_count + 1
        }));
        sendBIReport({
          ev: BI_EVENT_VIEW_COUNT,
          category: BI_CATEGORY_BEHAVIOR_IN_ROOM,
          a1: accountType,
          a2: location.href,
          a3: roomData.title,
          a4: roomData.hub_sid,
          a12: `${prevViewCount + 1}`
        });
        const iframeWindow = ref.current?.contentWindow;
        if (!iframeWindow) return;
        iframeWindow.postMessage({
          type: 'view-count-updated',
          payload: {
            viewCount: prevViewCount + 1
          }
        }, createDomain);
      }
    }
  };
  const checkWebGLSupport = (): boolean => {
    if (!window.WebGLRenderingContext) {
      return false;
    }
    const canvas = document.createElement('canvas');
    let gl = null;
    try {
      gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    } catch {
      return false;
    }
    if (!gl) {
      return false;
    }
    return true;
  };
  const {
    changeLocale
  } = useVSCSdkContext();
  useEffect(() => {
    const isSupported = checkWebGLSupport();
    setIsWebGLSupported(isSupported);
    if (!isSupported) {
      setIsShowSplash(false);
    } else if (!viverseProOwner) {
      setIsShowSplash(true);
    }
  }, [viverseProOwner]);
  const otherParams = Object.fromEntries(Array.from(searchParams).filter(([key]) => !Object.values(IFRAME_SEARCH_PARAM_KEYS).includes(key as IframeSearchParamKeys)));

  // 🥷🏼 Uncomment for local dev for in world (localhost:5173)
  // const createDomain = `https://localhost:5173`
  // const iframeUrl = `https://localhost:5173/${roomData.hub_sid}`
  const createDomain = `https://${getCreateDomain()}`;
  const iframeUrl = roomData.hubs_url;

  // Add JWT token to otherParams if available
  const iframeParams = {
    full3d: true,
    'no-cookie': true,
    view: enableViewMode,
    'no-scrolllock': disableScrollLock,
    otherParams: {
      ...otherParams,
      ...(jwtToken && {
        iframe_token: jwtToken
      })
    }
  };
  const iframeSrc = buildIframeUrl(iframeUrl, iframeParams);
  const {
    iframeSrc: activeIframeSrc,
    setIframeRef,
    teardownIframe
  } = useIframeSrcTeardown(ref, iframeSrc, {
    teardownOnPopstate: true
  });
  useMessageDispatch<WorldMessages>({
    'world-mounted': () => {
      setIsShowSplash(false);
      setIsLogoReadyToShow(true);
    },
    'sign-in': () => handleLogin({
      // 🥷🏼 if has full3d, add full3d= to the login url
      ...(enableFull3d && {
        loginParams: {
          full3d: ''
        }
      })
    }),
    'url-change': ({
      url
    }) => {
      handleUrlChange(url);
    },
    'world-exit': () => {
      // TODO: go back to origin's URL if needed
      const exitWorldUrl = getMobileRedirectUrl({
        isFull3d: false
      });
      handleUrlChange(exitWorldUrl);
    },
    'refresh-page': () => {
      window.location.reload();
    },
    'locale-changed': ({
      locale
    }) => {
      changeLocale(locale);
    },
    'debug-mode': ({
      configs
    }) => {
      window._CONFIGS = configs;
    },
    'done-update-vram': () => {
      window.dispatchEvent(new CustomEvent('done-update-vram'));
    },
    'fullscreen-request': ({
      enter
    }) => {
      if (enter) {
        const container = containerRef.current;
        if (!container || !document.fullscreenEnabled || typeof container.requestFullscreen !== 'function') return;
        void container.requestFullscreen().catch(error => {
          // eslint-disable-next-line no-console
          console.warn('[fullscreen] Failed to enter fullscreen:', error);
        });
      } else {
        void document.exitFullscreen?.().catch(error_0 => {
          // eslint-disable-next-line no-console
          console.warn('[fullscreen] Failed to exit fullscreen:', error_0);
        });
      }
    }
  }, createDomain);
  const handleUrlChange = (url_0: string) => {
    try {
      const targetUrl = new URL(url_0);
      const currentOrigin = window.location.origin;
      if (targetUrl.origin === currentOrigin) {
        // Same origin - use Next.js router for client-side navigation with i18n support
        teardownIframe();
        router.push(targetUrl.pathname + targetUrl.search + targetUrl.hash);
      } else {
        // Different origin - use window.location for full page navigation
        window.location.href = url_0;
      }
    } catch (error_1) {
      console.error('url-change error', error_1);
      // If URL parsing fails, treat as relative path
      teardownIframe();
      router.push(url_0);
    }
  };
  const handleIframeError = (e: SyntheticEvent<HTMLIFrameElement>) => {
    console.error('Iframe loading failed:', e);
    setIsShowSplash(false);
    setIsLogoReadyToShow(true);
  };
  const notifyChildWindowSize = useDebounceCallback(() => {
    const iframeWindow_0 = ref.current?.contentWindow;
    if (!iframeWindow_0) return;
    iframeWindow_0.postMessage({
      type: 'update-parent-window-size',
      payload: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    }, createDomain);
  }, 100);
  const handleIframeLoad = () => {
    notifyChildWindowSize();
  };

  // Notify PlayCanvas iframe about fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const iframeWindow_1 = ref.current?.contentWindow;
      if (!iframeWindow_1) return;
      iframeWindow_1.postMessage({
        type: 'parent-fullscreen-changed',
        payload: {
          isFullscreen: !!document.fullscreenElement
        }
      }, createDomain);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [createDomain, ref]);
  useEventListener('resize', notifyChildWindowSize);
  useTimeout(handleAddWorldViewCount, 3000);
  const allowAttr = getWorldIframeAllowPermissions().join('; ');

  // Show loading while checking WebGL support
  if (isWebGLSupported === null) {
    return null;
  }

  // Show WebGL error message if not supported
  if (isWebGLSupported === false) {
    return <div className="z-interactive size-full">
        <table style={{
        backgroundColor: '#8CE',
        width: '100%',
        height: '100%'
      }}>
          <tbody>
            <tr>
              <td align="center">
                <div style={{
                display: 'table-cell',
                verticalAlign: 'middle'
              }}>
                  <div>
                    This page requires a browser that supports WebGL.
                    <br />
                    <a href="https://get.webgl.org">Click here to find out more.</a>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>;
  }
  return <>
      <WorldSplash isVisible={isShowSplash} className={cn('absolute inset-0 z-20', {
      'max-md:hidden': !enableFull3d
    })} />

      {jwtToken !== null && <div ref={containerRef} className="relative size-full">
          {shouldRenderLogo && <ViverseLogoOverlay isVisible roomId={roomData?.hub_sid ?? ''} containerRef={containerRef} position={logoPosition} className={
      // Offset right to avoid PlayCanvas bottom-toolbar fullscreen button (desktop only,
      // viverse system enabled = toolbar visible)
      logoPosition === 'bottom-right' && !isMobile && !isViverseSystemDisabled ? 'right-24' : undefined} />}
          {activeIframeSrc && <iframe id="world-iframe" src={activeIframeSrc} ref={setIframeRef} title={roomData.title || 'World Experience'} className={cn('z-10 size-full border-none bg-transparent scheme-normal', {
        'hidden md:block': !enableFull3d
      })} onError={handleIframeError} onLoad={handleIframeLoad} allow={allowAttr} allowFullScreen {...handlers} />}
        </div>}
    </>;
}
export default WorldIframe;